<div *ngIf="visible" class="flex h-full min-h-0 flex-col gap-3">
  <p class="text-sm" style="color: var(--p-text-muted-color)">
    Move Account Users between Shipping Groups. <strong>Shipping Groups are account-wide</strong>, so
    this change applies to the user across every program, not only this one. A transfer replaces a
    user's existing Shipping Group memberships, and that cannot be undone.
  </p>

  <div *ngIf="loadError" class="rounded border border-red-300 bg-red-50 p-3 text-red-800">{{ loadError }}</div>

  <!-- One box, two states: who would move while ticking, what happened once it lands. -->
  <div *ngIf="selectedCount > 0" class="rounded border border-blue-300 bg-blue-50 p-3 text-blue-900">
    {{ movingMessage }}
    <div class="mt-1 text-sm">Drop them on a Shipping Group tile to move them.</div>
  </div>

  <div *ngIf="selectedCount === 0 && lastResultMessage"
    class="rounded border border-green-300 bg-green-50 p-3 text-green-900">
    {{ lastResultMessage }}
  </div>

  <div *ngIf="loading" class="flex items-center gap-2">
    <p-progressSpinner styleClass="h-6 w-6" strokeWidth="4" />
    <span>Loading…</span>
  </div>

  <div *ngIf="board?.DisqualifiedReason && !loading"
    class="rounded border border-amber-300 bg-amber-50 p-4 text-amber-900">
    {{ board!.DisqualifiedReason }}
  </div>

  <div *ngIf="board && !board.DisqualifiedReason && !loading" class="flex min-h-0 flex-1 gap-4">

    <!-- Roster -->
    <div class="flex w-[26rem] min-h-0 shrink-0 flex-col overflow-hidden rounded border border-gray-200">
      <div class="flex shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 p-2">
        <span class="font-semibold">Account Users</span>
        <span class="text-sm text-gray-600">
          {{ visibleUsers.length }} shown · {{ selectedCount }} selected
        </span>
      </div>

      <div class="flex shrink-0 flex-col gap-2 border-b border-gray-200 p-2 text-sm">
        <p-select [options]="groupFilterOptions" optionLabel="label" optionValue="value"
          [(ngModel)]="groupFilterId" [showClear]="true" [filter]="true" filterBy="label"
          [disabled]="transferring" placeholder="All Shipping Groups" styleClass="w-full" />

        <div class="flex items-center gap-2">
          <p-checkbox [binary]="true" [ngModel]="allSelectableSelected" (onChange)="toggleSelectAll()"
            inputId="shipTransferSelectAll" [disabled]="transferring" />
          <label for="shipTransferSelectAll">Select all{{ groupFilterId ? ' shown' : '' }}</label>
        </div>
      </div>

      <div class="roster min-h-0 flex-1">
        <div *ngFor="let u of visibleUsers" class="roster-row flex items-start gap-2 p-2"
          [class.selected]="isSelected(u)" pDraggable="shipTransferUsers"
          [pDraggableDisabled]="transferring" (onDragStart)="onDragStart(u)"
          (onDragEnd)="onRosterDragEnd()" (click)="toggleUser(u)">

          <p-checkbox [binary]="true" [ngModel]="isSelected(u)" [disabled]="transferring"
            (click)="$event.stopPropagation()" (onChange)="toggleUser(u)" />

          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="truncate font-medium">{{ u.Name }}</span>
              <span *ngIf="hasMoved(u)"
                class="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">moved</span>
              <!-- These are the users a transfer takes something away from, permanently. Badged
                   on the roster so it is visible while choosing, not only in the confirm. -->
              <span *ngIf="isMultiGroup(u)"
                class="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
                {{ u.CurrentGroupNames.length }} groups
              </span>
            </div>
            <div class="truncate text-xs text-gray-600">
              {{ u.CurrentGroupNames.length > 0 ? u.CurrentGroupNames.join(', ') : 'No shipping group' }}
            </div>
          </div>
        </div>

        <div *ngIf="board.Users.length === 0" class="p-4 text-center text-gray-600">
          No Account Users reach this program.
        </div>
        <div *ngIf="board.Users.length > 0 && visibleUsers.length === 0" class="p-4 text-center text-gray-600">
          No Account Users match this filter.
        </div>
      </div>
    </div>

    <!-- Tiles -->
    <div class="flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-gray-200">
      <div class="flex shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 p-2">
        <span class="font-semibold">Shipping Groups</span>
        <span class="text-sm text-gray-600">{{ board.Tiles.length }} groups</span>
      </div>
      <div class="min-h-0 flex-1 overflow-y-auto p-3">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div *ngFor="let tile of board.Tiles" class="qb-detail-card flex flex-col gap-2 p-4"
            [class.tile-drop-target]="dragOverTileId === tile.Id" [class.tile-disabled]="!canReceive(tile)"
            pDroppable="shipTransferUsers" (onDragEnter)="onDragEnter(tile)" (onDragLeave)="onDragLeave(tile)"
            (onDrop)="onDrop(tile)">

            <div class="flex items-start justify-between gap-2">
              <span class="font-semibold">{{ tile.Name }}</span>
              <span class="shrink-0 text-sm text-gray-600">{{ tile.MemberCount }} here</span>
            </div>

            <!-- What differs between shipping groups is the addresses they permit. -->
            <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <span class="text-gray-500">Addresses</span>
              <span>{{ tile.AddressCount }}</span>

              <span class="text-gray-500">Ships to</span>
              <span>{{ addressSummary(tile) }}</span>
            </div>

            <div *ngIf="tile.CannotReceiveReason"
              class="mt-1 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
              {{ tile.CannotReceiveReason }}
            </div>

            <div *ngIf="tile.WarningReason && !tile.CannotReceiveReason"
              class="mt-1 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
              {{ tile.WarningReason }}
            </div>

            <div *ngIf="!tile.CannotReceiveReason" class="mt-1 text-xs text-gray-500">
              Drop {{ selectedCount > 0 ? selectedCount : 'selected' }}
              {{ selectedCount === 1 ? 'user' : 'users' }} here
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
